/**
 * One drawing for every tree (design study 12, 2026-10-06; Jason: "the client view of the tree, and the per project
 * admin tree configuration to all use the same styles that our master has been iterating on, the only thing is the
 * differences in how the per project tree is adopted"): the master tree on its page, a job's on its Process page, the
 * client's on their home. Each lays its steps out the same way, from the steps and what each waits on alone:
 *
 * - Rows (`gridRows`, Tidy's rule, design study 09): the phases one under another, and in each its steps in rows, a
 *   step on the row of the longest way back to it within its phase, so nothing in a row waits on anything else in it;
 *   one with nothing before it in its phase comes down to just above what waits on it there (P-19). The step that
 *   closes a phase ends it, alone on its last row. Across (#78), every line as the board draws it (the board's own
 *   account, else `drawnLines`: through the gates between the phases, a line a longer way already draws left out) has
 *   a lane in each row it passes, and the rows are ordered for the fewest crossings, then the shortest lines.
 * - Across (`gridColumns`, #78, Jason, 2026-10-22: "the proximity is irrelevant"): no fixed columns; every step and lane
 *   placed in its row's order as near what it joins above and below as the room between them allows, a chain and a
 *   long line's lanes held straight down, the start, the end and each phase's closer in the middle, the drawing as
 *   wide as that needs. A row is never carried on under itself (P-19, Jason, 2026-10-09: "the dependencies alone
 *   should determine what could occure at the same time"), so a step's row is only ever where its waits put it.
 * - Lines (`directNeeds`): a line a longer way already draws is left out of the drawing (study 10, lines C); the tree
 *   keeps it (study 12, shortcuts B).
 * - A step opened (`chainRows`, study 10, open; study 12, open A): its chain alone, what it waits on above it a row a
 *   step back, what waits on it below.
 *
 * Positions are columns (a column is a step's width and the gap beside it) and rows, never pixels: the screen turns
 * them into a drawing (web `components/tree/`).
 */

/** A step to lay out: its key, its phase (one of the phases, else in none), what it waits on, and which end it is. */
export interface GridStep {
  key: string;
  phase: string | null;
  /** Keys of the steps it waits on; any that are not among the steps are passed over. */
  needs: readonly string[];
  /** The start heads everything, the end follows everything, each alone in the middle. */
  end?: 'start' | 'end';
}

/** A phase, in order: the step that closes it, when it has one. */
export interface GridPhase {
  key: string;
  closedBy: string | null;
}

/**
 * The steps in rows: each phase's rows in order (the steps in no phase last), the start above and the end below; a
 * phase's closer, where it is alone at its foot (the gate drawn between it and the next, P-17).
 */
export interface GridRows extends GridRanks {
  /**
   * What each step waits on as its lines are drawn (`GridRowsOptions.lines`, else `drawnLines`): through the gates
   * between the phases, what a longer way already draws left out. The rows are ordered by these.
   */
  drawn: ReadonlyMap<string, readonly string[]>;
  /** Every row from the top down, the start's and the end's too: its steps and the lanes of the lines passing it, in order. */
  layers: GridLayer[];
  /** Each line as drawn: what it leaves and what it comes into, and its lane in each row it passes on the way, from the top. */
  lines: { from: string; to: string; via: string[] }[];
}

/** The steps in rows before they are ordered across: what the board is given to say which lines it draws. */
export interface GridRanks {
  start: string | null;
  end: string | null;
  groups: { phase: string | null; rows: string[][]; closer?: string }[];
}

/** A row from the top down: the start's, the end's, or a phase's row (`group` its place in `GridRows.groups`). */
export interface GridLayer {
  at: 'start' | 'end' | { group: number; row: number };
  /** Its steps, and a lane (a key starting `~`) for each line passing it. */
  keys: string[];
}

export interface GridRowsOptions {
  /** Keep a phase with no steps, for a drawing that shows every phase. */
  keepEmpty?: boolean;
  /** The lines the board draws, each `[from, to]`, given the steps in rows: else core's own account of them (`drawnLines`). */
  lines?: (ranks: GridRanks) => readonly (readonly [string, string])[];
}

/** How many times the rows are ordered against the ones above and below before the shortest lines are kept. */
const SWEEPS = 8;
/** How many times every step and lane is moved to its best place in its row, at most. */
const SIFTS = 1;
/** How many times the rows are swept, then sifted, from one first order, at most. */
const ROUNDS = 2;

/**
 * The steps in rows (Tidy's rule): `keepEmpty` keeps a phase with no steps, for a drawing that shows every phase
 * (one a step can be dropped into). The order the steps come in is the order across a row before it is settled.
 */
export function gridRows(phases: readonly GridPhase[], steps: readonly GridStep[], opts: GridRowsOptions = {}): GridRows {
  const start = steps.find((s) => s.end === 'start') ?? null;
  const end = steps.find((s) => s.end === 'end') ?? null;
  const placed = steps.filter((s) => !s.end);
  const index = new Map(steps.map((s, i) => [s.key, i]));
  const phaseKeys = new Set(phases.map((p) => p.key));
  const known = (s: GridStep) => s.phase !== null && phaseKeys.has(s.phase);
  const groups = [
    ...phases.map((p) => ({ key: p.key as string | null, closedBy: p.closedBy, steps: placed.filter((s) => s.phase === p.key) })),
    { key: null as string | null, closedBy: null as string | null, steps: placed.filter((s) => !known(s)) },
  ].filter((g) => g.steps.length || (g.key !== null && opts.keepEmpty));
  const all = new Set(steps.map((s) => s.key));
  const needsOf = new Map(steps.map((s) => [s.key, [...new Set(s.needs)].filter((d) => all.has(d) && d !== s.key)]));

  // Each phase's rows: a step's row is the longest way back to it within its phase; the step that closes the phase
  // waits on everything in it but what comes after the closer (Jason, 2026-10-06, of Design proposal closing Discovery:
  // "make sure this is hendled with the right node layout").
  const rows: { group: string | null; keys: string[] }[] = [];
  for (const g of groups) {
    const inGroup = new Set(g.steps.map((s) => s.key));
    const closer = g.closedBy !== null && inGroup.has(g.closedBy) ? g.closedBy : null;
    const afterCloser = new Set<string>();
    if (closer) {
      for (let grew = true; grew; ) {
        grew = false;
        for (const s of g.steps) {
          if (!afterCloser.has(s.key) && needsOf.get(s.key)!.some((d) => d === closer || afterCloser.has(d))) {
            afterCloser.add(s.key);
            grew = true;
          }
        }
      }
    }
    const needsHere = (k: string) => {
      const real = needsOf.get(k)!.filter((d) => inGroup.has(d));
      return k === closer ? [...new Set([...real, ...g.steps.map((s) => s.key).filter((x) => x !== k && !afterCloser.has(x))])] : real;
    };
    const rank = new Map<string, number>();
    const rankOf = (k: string, seen: Set<string>): number => {
      const got = rank.get(k);
      if (got !== undefined) return got;
      if (seen.has(k)) return 0;
      seen.add(k);
      const deps = needsHere(k);
      const r = deps.length ? 1 + Math.max(...deps.map((d) => rankOf(d, seen))) : 0;
      rank.set(k, r);
      return r;
    };
    for (const s of g.steps) rankOf(s.key, new Set());
    // A step with nothing before it in its phase comes down to just above the earliest step of its phase that waits on
    // it, never left at the phase's top (P-19, Jason, 2026-10-09: "the hard rule is that a nodes position on the tree is
    // driven by dependences, all the off ones seem to float to the top of the phase and then move around"); one nothing
    // in its phase waits on (what waits on it comes later, or nothing does) sits as low as the phase's own steps go,
    // above its closer. Only those with nothing before them move, so no wait in the phase ever points up.
    const waitsOn = new Map<string, string[]>();
    for (const s of g.steps) for (const d of needsOf.get(s.key)!) if (inGroup.has(d) && s.key !== closer) waitsOn.set(d, [...(waitsOn.get(d) ?? []), s.key]);
    const lowest = Math.max(0, ...g.steps.filter((s) => s.key !== closer && !afterCloser.has(s.key)).map((s) => rank.get(s.key)!));
    for (const s of g.steps) {
      if (s.key === closer || needsOf.get(s.key)!.some((d) => inGroup.has(d))) continue;
      const after = (waitsOn.get(s.key) ?? []).map((d) => rank.get(d)!);
      rank.set(s.key, after.length ? Math.max(rank.get(s.key)!, Math.min(...after) - 1) : Math.max(rank.get(s.key)!, lowest));
    }
    const deepest = Math.max(-1, ...g.steps.map((s) => rank.get(s.key)!));
    const mine: string[][] = Array.from({ length: deepest + 1 }, () => []);
    for (const s of [...g.steps].sort((a, b) => index.get(a.key)! - index.get(b.key)!)) mine[rank.get(s.key)!]!.push(s.key);
    if (!mine.length) rows.push({ group: g.key, keys: [] });
    for (const r of mine) rows.push({ group: g.key, keys: r });
  }

  // Only the rows something is on: a rank nothing came down to is no row (an empty phase keeps its one, to be drawn).
  for (let i = rows.length - 1; i >= 0; i--) if (!rows[i]!.keys.length && rows.some((r, j) => j !== i && r.group === rows[i]!.group)) rows.splice(i, 1);
  const groupsOf = () =>
    groups.map((g) => {
      const mine = rows.filter((r) => r.group === g.key && r.keys.length).map((r) => [...r.keys]);
      const foot = mine.at(-1);
      const closer = g.closedBy !== null && foot?.length === 1 && foot[0] === g.closedBy ? g.closedBy : null;
      return { phase: g.key, rows: mine, ...(closer ? { closer } : {}) };
    });

  // Across (#78, Jason, 2026-10-09: "there needs to be some sense to have as few crossings as possible to maintain a
  // tidy arrangement"; 2026-10-22: "i mean generalize this, there should be a clean algorithm to untangle this stuff so
  // that there are not crossings"): the layered drawing's way. The lines as the board draws them (`opts.lines`, the
  // board's own account; else `drawnLines`), not waits it never draws; every line that passes rows on its way has a
  // place in each (a lane), so a long line is ordered with the steps it passes as a short one is, and two lines cross
  // exactly where their ends between one row and the next come in opposite orders. Every row ordered against its
  // neighbours, each step under the middle of what it needs (going down) or over the middle of what needs it (going up),
  // the start and the end in the middle, the order whose lines cross least between neighbouring rows kept, then the one
  // whose lines run shortest; each long line laid again whole where it crosses fewest (`relay`); each step and lane
  // moved, one at a time, to wherever in its row its lines cross least; neighbours in a row swapped while that crosses
  // less; over again till nothing helps.
  const ranks: GridRanks = { start: start?.key ?? null, end: end?.key ?? null, groups: groupsOf() };
  const keyed = new Set(steps.map((s) => s.key));
  const seenEdge = new Set<string>();
  const edges = (opts.lines ? opts.lines(ranks) : drawnLines(groups, rows, start?.key ?? null, end?.key ?? null, steps, needsOf))
    .map(([a, b]) => [a, b] as [string, string])
    .filter(([a, b]) => {
      const id = `${a}>${b}`;
      if (!keyed.has(a) || !keyed.has(b) || a === b || seenEdge.has(id)) return false;
      seenEdge.add(id);
      return true;
    });
  const layers: string[][] = [...(start ? [[start.key]] : []), ...rows.map((r) => [...r.keys]), ...(end ? [[end.key]] : [])];
  const at: GridLayer['at'][] = [
    ...(start ? ['start' as const] : []),
    ...rows.map((r, i) => ({ group: groups.findIndex((g) => g.key === r.group), row: rows.slice(0, i).filter((x) => x.group === r.group).length })),
    ...(end ? ['end' as const] : []),
  ];
  const layerOf = new Map<string, number>();
  layers.forEach((l, i) => l.forEach((k) => layerOf.set(k, i)));
  // A line's places in the rows it passes: `~` keys no step has.
  const down = new Map<string, string[]>();
  const up = new Map<string, string[]>();
  const link = (a: string, b: string) => {
    down.set(a, [...(down.get(a) ?? []), b]);
    up.set(b, [...(up.get(b) ?? []), a]);
  };
  const lines: GridRows['lines'] = [];
  edges.forEach(([a0, b0], e) => {
    if (!layerOf.has(a0) || !layerOf.has(b0) || layerOf.get(a0) === layerOf.get(b0)) return;
    const [a, b] = layerOf.get(a0)! < layerOf.get(b0)! ? [a0, b0] : [b0, a0];
    let prev = a;
    const via: string[] = [];
    for (let l = layerOf.get(a)! + 1; l < layerOf.get(b)!; l++) {
      const lane = `~${e}:${l}`;
      layers[l]!.push(lane);
      layerOf.set(lane, l);
      via.push(lane);
      link(prev, lane);
      prev = lane;
    }
    link(prev, b);
    lines.push({ from: a0, to: b0, via });
  });
  const xOf = new Map<string, number>();
  const settle = (l: string[]) => l.forEach((k, i) => xOf.set(k, i - (l.length - 1) / 2));
  for (const l of layers) settle(l);
  // The lines between a row and the next, as their ends' places, and how many of them cross.
  const between = (l: number) => layers[l]!.flatMap((a) => (down.get(a) ?? []).map((b) => [a, b] as const));
  const crossesAt = (l: number) => {
    if (l < 0 || l + 1 >= layers.length) return 0;
    const tops: number[] = [];
    const feet: number[] = [];
    for (const a of layers[l]!)
      for (const b of down.get(a) ?? []) {
        tops.push(xOf.get(a)!);
        feet.push(xOf.get(b)!);
      }
    let n = 0;
    for (let i = 0; i < tops.length; i++) for (let j = i + 1; j < tops.length; j++) if ((tops[i]! - tops[j]!) * (feet[i]! - feet[j]!) < 0) n++;
    return n;
  };
  const crossings = () => layers.reduce((n, _, l) => n + crossesAt(l), 0);
  const length = () => layers.reduce((n, _, l) => n + between(l).reduce((m, [a, b]) => m + Math.abs(xOf.get(a)! - xOf.get(b)!), 0), 0);
  const better = (a: { crossings: number; length: number }, b: { crossings: number; length: number }) =>
    a.crossings < b.crossings || (a.crossings === b.crossings && a.length < b.length);
  const fixed = (l: number) => layers[l]!.length === 1 && (layers[l]![0] === start?.key || layers[l]![0] === end?.key);
  const reorder = (l: number, toward: Map<string, string[]>) => {
    if (fixed(l)) return;
    const r = layers[l]!;
    const pull = (k: string) => {
      const xs = (toward.get(k) ?? []).map((x) => xOf.get(x)!);
      return xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : xOf.get(k)!;
    };
    const at0 = new Map(r.map((k, i) => [k, i]));
    const pulled = new Map(r.map((k) => [k, pull(k)]));
    r.sort((a, b) => pulled.get(a)! - pulled.get(b)! || at0.get(a)! - at0.get(b)!);
    settle(r);
  };
  const snapshot = () => ({ crossings: crossings(), length: length(), order: layers.map((l) => [...l]) });
  const restore = (order: readonly string[][]) =>
    order.forEach((o, l) => {
      layers[l] = [...o];
      settle(layers[l]!);
    });
  // Rows ordered against the ones above, then the ones below, over and over, the best order met kept.
  const sweeps = () => {
    let best = snapshot();
    for (let sweep = 0; sweep < SWEEPS; sweep++) {
      if (sweep % 2 === 0) for (let l = 0; l < layers.length; l++) reorder(l, up);
      else for (let l = layers.length - 1; l >= 0; l--) reorder(l, down);
      const now = snapshot();
      if (better(now, best)) best = now;
    }
    restore(best.order);
  };
  // Where a step's lines go, up and down; and how many of two steps' lines cross, the first left of the second.
  const ends = (k: string): readonly [number[], number[]] => [(up.get(k) ?? []).map((x) => xOf.get(x)!), (down.get(k) ?? []).map((x) => xOf.get(x)!)];
  const crossed = (a: readonly [number[], number[]], b: readonly [number[], number[]]) => {
    let n = 0;
    for (let side = 0; side < 2; side++) for (const x of a[side]!) for (const y of b[side]!) if (x > y) n++;
    return n;
  };
  const pairCrossings = (a: string, b: string) => crossed(ends(a), ends(b));
  // Each step and lane moved to wherever in its row its lines cross least (sifting), while anything moves.
  const sift = () => {
    for (let pass = 0, moved = true; moved && pass < SIFTS; pass++) {
      moved = false;
      for (const dir of [1, -1])
        for (let n = 0; n < layers.length; n++) {
          const l = dir > 0 ? n : layers.length - 1 - n;
          if (fixed(l) || layers[l]!.length < 2) continue;
          // The rows either side stay put while this one is sifted.
          const at = new Map(layers[l]!.map((k) => [k, ends(k)]));
          for (const k of [...layers[l]!]) {
            const r = layers[l]!;
            const was = r.indexOf(k);
            const others = r.filter((o) => o !== k);
            const left = others.map((o) => crossed(at.get(o)!, at.get(k)!));
            const right = others.map((o) => crossed(at.get(k)!, at.get(o)!));
            // Its crossings with the rest at each place it could take, from first to last.
            let cost = right.reduce((s, c) => s + c, 0);
            const costs = [cost];
            for (let p = 1; p <= others.length; p++) {
              cost += left[p - 1]! - right[p - 1]!;
              costs.push(cost);
            }
            const bestAt = costs.indexOf(Math.min(...costs));
            if (costs[bestAt]! < costs[was]!) {
              others.splice(bestAt, 0, k);
              layers[l] = others;
              settle(others);
              moved = true;
            }
          }
        }
    }
  };
  // Neighbours swapped while the swap crosses less, or as little and runs shorter, a few times over.
  const swaps = () => {
    for (let pass = 0, swapped = true; swapped && pass < 4; pass++) {
      swapped = false;
      for (const r of layers)
        for (let i = 0; i + 1 < r.length; i++) {
          const [a, b] = [r[i]!, r[i + 1]!];
          // What the swap changes: how the two's own lines cross, and how far they run.
          const crossing = pairCrossings(b, a) - pairCrossings(a, b);
          if (crossing > 0) continue;
          const len = (k: string) => [...(up.get(k) ?? []), ...(down.get(k) ?? [])].reduce((m, x) => m + Math.abs(xOf.get(x)! - xOf.get(k)!), 0);
          const [xa, xb] = [xOf.get(a)!, xOf.get(b)!];
          const was = len(a) + len(b);
          xOf.set(a, xb);
          xOf.set(b, xa);
          if (crossing < 0 || len(a) + len(b) < was) {
            [r[i], r[i + 1]] = [b, a];
            swapped = true;
          } else {
            xOf.set(a, xa);
            xOf.set(b, xb);
          }
        }
    }
  };
  // The order a walk down the lines from the top meets the steps and lanes in (from the last line out of each first, when
  // `back`), each row in that order.
  const walked = (back: boolean) => {
    const met = new Map<string, number>();
    const walk = (k: string) => {
      if (met.has(k)) return;
      met.set(k, met.size);
      const next = down.get(k) ?? [];
      for (const n of back ? [...next].reverse() : next) walk(n);
    };
    for (const l of layers) for (const k of back ? [...l].reverse() : l) walk(k);
    return layers.map((l) => [...l].sort((a, b) => met.get(a)! - met.get(b)!));
  };
  // Each long line laid again whole, the rest held where they are: in each row it passes, the place between two of that
  // row's steps and lanes where it crosses fewest of the others, found row by row (a shortest way down), so a line that
  // would cut across a run of others in one row goes round them instead.
  const relay = () => {
    let moved = false;
    for (const line of lines) {
      if (line.via.length < 2) continue;
      const own = new Set(line.via);
      const [top, bottom] = layerOf.get(line.from)! < layerOf.get(line.to)! ? [line.from, line.to] : [line.to, line.from];
      const l0 = layerOf.get(top)!;
      const l1 = layerOf.get(bottom)!;
      const was = line.via.map((v) => layers[layerOf.get(v)!]!.indexOf(v));
      for (const v of line.via) {
        const r = layers[layerOf.get(v)!]!;
        r.splice(r.indexOf(v), 1);
      }
      const index = (k: string) => layers[layerOf.get(k)!]!.indexOf(k);
      // The others' lines between a row and the next, as their two ends' places.
      const others = (l: number) => {
        const out: [number, number][] = [];
        layers[l]!.forEach((a, i) => {
          for (const b of down.get(a) ?? []) if (!own.has(b) && !own.has(a)) out.push([i, layers[l + 1]!.indexOf(b)]);
        });
        return out;
      };
      // Best so far to each place in each row (the place just before the step or lane at that index), and the place in
      // the row above it came from.
      let cost: number[] = [0];
      let from: number[] = [index(top)];
      const back: number[][] = [];
      for (let l = l0 + 1; l <= l1; l++) {
        const segs = others(l - 1);
        const width = layers[l]!.length;
        const to = l === l1 ? [index(bottom)] : Array.from({ length: width + 1 }, (_, s) => s - 0.5);
        const next = to.map(() => Infinity);
        const came = to.map(() => 0);
        from.forEach((p, pi) => {
          // How many of the others' lines between the two rows this one crosses from p to each place below: those that
          // leave left of p and come in right of it, and those that leave right and come in left.
          const right = Array<number>(width + 1).fill(0);
          const left = Array<number>(width + 1).fill(0);
          for (const [a, b] of segs) {
            if (a < p) right[b]!++;
            else if (a > p) left[b]!++;
          }
          for (let i = width - 1; i >= 0; i--) right[i]! += right[i + 1]!;
          for (let i = 1; i <= width; i++) left[i]! += left[i - 1]!;
          to.forEach((q, qi) => {
            const crossed = l === l1 ? (right[q + 1] ?? 0) + (q > 0 ? left[q - 1]! : 0) : right[qi]! + (qi > 0 ? left[qi - 1]! : 0);
            // Ties keep the line where it was.
            const stay = l < l1 && was[l - l0 - 1] === qi ? -1e-6 : 0;
            const c = cost[pi]! + crossed + stay;
            if (c < next[qi]!) {
              next[qi] = c;
              came[qi] = pi;
            }
          });
        });
        back.push(came);
        cost = next;
        from = to;
      }
      const at: number[] = [];
      for (let l = back.length - 1, k = 0; l > 0; l--) {
        k = back[l]![k]!;
        at.unshift(k);
      }
      line.via.forEach((v, i) => {
        const r = layers[layerOf.get(v)!]!;
        r.splice(Math.min(at[i]!, r.length), 0, v);
        settle(r);
      });
      if (at.some((s, i) => s !== was[i])) moved = true;
      for (let l = l0 + 1; l < l1; l++) settle(layers[l]!);
    }
    return moved;
  };
  // Tried from four first orders (the one given, its mirror, and a walk's each way), each swept, sifted and swapped in
  // turn till that helps no more. The rows between two of one step alone (a gate, an end) cross only among themselves:
  // each such stretch takes its order from whichever try crossed least there, then ran shortest.
  const tries: { order: string[][]; at: number[]; long: number[] }[] = [];
  const pickTries = String((globalThis as unknown as { __tries?: string }).__tries ?? "0123");
  for (const first of [layers.map((l) => [...l]), layers.map((l) => [...l].reverse()), walked(false), walked(true)].filter((_, i) => pickTries.includes(String(i)))) {
    restore(first);
    let was = snapshot();
    for (let round = 0; round < ROUNDS; round++) {
      sweeps();
      relay();
      sift();
      swaps();
      const now = snapshot();
      if (!better(now, was)) break;
      was = now;
    }
    restore(was.order);
    tries.push({
      order: was.order,
      at: layers.map((_, l) => crossesAt(l)),
      long: layers.map((_, l) => between(l).reduce((m, [a, b]) => m + Math.abs(xOf.get(a)! - xOf.get(b)!), 0)),
    });
  }
  const chosen: string[][] = [];
  for (let l = 0; l < layers.length; ) {
    let z = l + 1;
    while (z < layers.length && layers[z]!.length > 1) z++;
    // Rows l..z, z alone (or past the last): the lines between them are this stretch's.
    const sum = (t: (typeof tries)[number], of: 'at' | 'long') => t[of].slice(l, Math.min(z, layers.length - 1)).reduce((n, v) => n + v, 0);
    const pick = [...tries].sort((p, q) => sum(p, 'at') - sum(q, 'at') || sum(p, 'long') - sum(q, 'long'))[0]!;
    for (let i = l; i < Math.min(z, layers.length); i++) chosen[i] = pick.order[i]!;
    l = z;
  }
  restore(chosen);
  // The steps' own order in each row, the lanes' places dropped.
  const offset = start ? 1 : 0;
  rows.forEach((r, i) => {
    r.keys = layers[i + offset]!.filter((k) => !k.startsWith('~'));
  });
  const drawn = new Map<string, string[]>();
  for (const [d, s] of edges) drawn.set(s, [...(drawn.get(s) ?? []), d]);
  return {
    start: start?.key ?? null,
    end: end?.key ?? null,
    drawn,
    layers: layers.map((keys, i) => ({ at: at[i]!, keys })),
    lines,
    groups: groupsOf(),
  };
}

/**
 * The lines as the board draws them, each `[from, to]` (#78: the rows are ordered against what is drawn, not against
 * waits nobody sees): a line to a step of a later phase drawn from the gate docked just before that phase, the step it
 * leaves drawn into its own phase's gate (P-17; web `linesThrough`), and a line a longer way already draws left out
 * (`directNeeds`). A gate docks where it closes its phase alone at its foot and another phase follows; the start docks
 * on the first.
 */
function drawnLines(
  groups: readonly { key: string | null; closedBy: string | null }[],
  rows: readonly { group: string | null; keys: string[] }[],
  start: string | null,
  end: string | null,
  steps: readonly GridStep[],
  needsOf: ReadonlyMap<string, string[]>,
): [string, string][] {
  const phased = groups.filter((g) => g.key !== null);
  const place = new Map<string, number>();
  if (start) place.set(start, -1);
  if (end) place.set(end, phased.length);
  for (const s of steps) {
    const i = phased.findIndex((g) => g.key === s.phase);
    if (!s.end && i >= 0) place.set(s.key, i);
  }
  const gate = new Map<number, string>();
  if (start && phased.length && groups[0]!.key !== null) gate.set(-1, start);
  phased.forEach((g, i) => {
    const foot = rows.filter((r) => r.group === g.key && r.keys.length).at(-1)?.keys;
    if (i + 1 < phased.length && g.closedBy !== null && foot?.length === 1 && foot[0] === g.closedBy) gate.set(i, g.closedBy);
  });
  const out: [string, string][] = [];
  const own = new Map<string, string>();
  for (const s of steps)
    for (const d of needsOf.get(s.key)!) {
      const a = place.get(d);
      const b = place.get(s.key);
      const mine = a === undefined ? undefined : gate.get(a);
      if (a === undefined || b === undefined || a >= b || mine === undefined) {
        out.push([d, s.key]);
        continue;
      }
      let k = b - 1;
      while (k > a && !gate.has(k)) k--;
      const g = gate.get(k)!;
      if (g === d) {
        out.push([d, s.key]);
        continue;
      }
      out.push([g, s.key]);
      if (mine !== d) own.set(d, mine);
    }
  for (const [d, g] of own) if (!out.some(([x]) => x === d)) out.push([d, g]);
  const into = new Map<string, string[]>();
  for (const [d, s] of out) into.set(s, [...new Set([...(into.get(s) ?? []), d])]);
  const direct = directNeeds([...into].map(([key, needs]) => ({ key, needs })).concat(steps.filter((s) => !into.has(s.key)).map((s) => ({ key: s.key, needs: [] }))));
  return [...direct].flatMap(([s, ds]) => ds.map((d) => [d, s] as [string, string]));
}

/** A row as drawn: its steps and the column each takes (a column's middle; between two columns is a place too). */
export interface GridLine {
  keys: string[];
  cols: number[];
  /** The row it is, counted down its phase from 0. */
  row: number;
}

/** A line's lane in a row it passes: the row (`block` its phase's place in `GridPlan.blocks`), and where across. */
export interface GridLanePlace {
  block: number;
  row: number;
  col: number;
}

/** The steps placed across: the start and end in the middle, each phase's rows as lines, each long line's lanes. */
export interface GridPlan {
  /** How many columns wide the drawing is: the window's, or as many as what is drawn needs where that is more. */
  cols: number;
  start: { key: string; col: number } | null;
  end: { key: string; col: number } | null;
  blocks: { phase: string | null; lines: GridLine[] }[];
  /** Each line that passes rows on its way: its lane in each, from the top down, where it runs straight down. */
  lanes: { from: string; to: string; via: GridLanePlace[] }[];
}

/** How far apart, middle to middle, in columns: two steps side by side; a step and a lane beside it; two lanes. */
export interface GridSpace {
  steps: number;
  side: number;
  lanes: number;
}

export const GRID_SPACE: GridSpace = { steps: 1, side: 0.5, lanes: 0.08 };

/**
 * Values in order, each as near its own as it can be while none comes before the one ahead of it (the least weighted
 * squared distance; pool adjacent violators).
 */
function ordered(want: readonly number[], weight: readonly number[]): number[] {
  const pools: { v: number; w: number; n: number }[] = [];
  want.forEach((v, i) => {
    pools.push({ v, w: weight[i]!, n: 1 });
    while (pools.length > 1 && pools.at(-2)!.v > pools.at(-1)!.v) {
      const b = pools.pop()!;
      const a = pools.pop()!;
      pools.push({ v: (a.v * a.w + b.v * b.w) / (a.w + b.w), w: a.w + b.w, n: a.n + b.n });
    }
  });
  return pools.flatMap((p) => Array<number>(p.n).fill(p.v));
}

/** How many times the rows are placed against the ones above, then the ones below. */
const PLACINGS = 10;
/** How far aside, in columns, a step goes from right under one whose line crosses its own. */
const PART = 0.15;

/**
 * The rows placed across (#78, Jason, 2026-10-22: "make space between the notes and move them around to as required to
 * avoid crossings, the proximity is irrelevant"): every step and every lane in the order the rows were given, each as
 * near the middle of what it joins in the rows above and below as the room between them allows (a step and the next
 * `space.steps` apart, a lane `space.side` from a step and `space.lanes` from another lane; a priority method, rows
 * placed over and over against the ones above, then below, each by least squares in its order), a line that is the only
 * one out of the one above and into the one below held hardest, so a chain runs straight down, and a long line's lanes
 * lined up one under another wherever the rows it passes have room; where two lines must cross and one comes down right
 * over the step the other goes into, that step moved a little aside, so they cross once, plainly; the start, the end
 * and a phase's closer alone at its foot in the middle. The drawing is as wide as that needs, or the window's `cols`
 * where that is more: a row is never carried on under itself (P-19).
 */
export function gridColumns(rows: GridRows, cols: number, space: GridSpace = GRID_SPACE): GridPlan {
  const layers = rows.layers;
  const lane = (k: string) => k.startsWith('~');
  const middle = new Set([rows.start, rows.end, ...rows.groups.map((g) => g.closer ?? null)].filter((k): k is string => k !== null));
  const layerOf = new Map<string, number>();
  layers.forEach((l, i) => l.keys.forEach((k) => layerOf.set(k, i)));
  const up = new Map<string, string[]>();
  const down = new Map<string, string[]>();
  for (const line of rows.lines) {
    if (!layerOf.has(line.from) || !layerOf.has(line.to)) continue;
    const ends = layerOf.get(line.from)! < layerOf.get(line.to)! ? [line.from, line.to] : [line.to, line.from];
    const chain = [ends[0]!, ...line.via, ends[1]!];
    for (let i = 1; i < chain.length; i++) {
      down.set(chain[i - 1]!, [...(down.get(chain[i - 1]!) ?? []), chain[i]!]);
      up.set(chain[i]!, [...(up.get(chain[i]!) ?? []), chain[i - 1]!]);
    }
  }
  // A line that is the only one out of the one above and the only one into the one below holds hardest, so a chain and a
  // long line's lanes run straight down; then a lane to a step at its line's end; then a step to a step.
  const only = (a: string, b: string) => {
    const [hi, lo] = layerOf.get(a)! < layerOf.get(b)! ? [a, b] : [b, a];
    return (down.get(hi) ?? []).length === 1 && (up.get(lo) ?? []).length === 1;
  };
  const pull = (a: string, b: string) => (only(a, b) ? 10000 : lane(a) && lane(b) ? 8 : lane(a) || lane(b) ? 2 : 1);
  const apart = (a: string, b: string) => (lane(a) && lane(b) ? space.lanes : lane(a) || lane(b) ? space.side : space.steps);
  const x = new Map<string, number>();
  const room = (keys: readonly string[]) => keys.map((_, i) => (i ? apart(keys[i - 1]!, keys[i]!) : 0)).reduce<number[]>((s, d, i) => [...s, (s[i - 1] ?? 0) + d], []);
  for (const l of layers) {
    const at = room(l.keys);
    const half = (at.at(-1) ?? 0) / 2;
    l.keys.forEach((k, i) => x.set(k, at[i]! - half));
  }
  const place = (l: readonly string[], sides: readonly Map<string, string[]>[]) => {
    const want: number[] = [];
    const weight: number[] = [];
    for (const k of l) {
      if (middle.has(k)) {
        want.push(0);
        weight.push(1e6);
        continue;
      }
      let sum = 0;
      let w = 0;
      for (const side of sides)
        for (const n of side.get(k) ?? []) {
          sum += pull(k, n) * x.get(n)!;
          w += pull(k, n);
        }
      want.push(w ? sum / w : x.get(k)!);
      weight.push(w || 0.05);
    }
    const at = room(l);
    const placed = ordered(
      want.map((v, i) => v - at[i]!),
      weight,
    );
    l.forEach((k, i) => x.set(k, placed[i]! + at[i]!));
  };
  for (let n = 0; n < PLACINGS; n++) {
    for (const l of layers) place(l.keys, [up]);
    for (const l of [...layers].reverse()) place(l.keys, [down]);
  }
  for (const l of layers) place(l.keys, [up, down]);
  // Little turns taken out: a step or lane moved right over (or under) what it joins, where its row has the room, when
  // more of its lines then run straight down than did.
  const straight = (k: string, at: number) => [...(up.get(k) ?? []), ...(down.get(k) ?? [])].reduce((s, n) => s + (Math.abs(x.get(n)! - at) < 1e-6 ? pull(k, n) : 0), 0);
  for (let pass = 0, moved = true; moved && pass < 4; pass++) {
    moved = false;
    for (const l of layers)
      l.keys.forEach((k, i) => {
        if (middle.has(k)) return;
        const now = x.get(k)!;
        const lo = i > 0 ? x.get(l.keys[i - 1]!)! + apart(l.keys[i - 1]!, k) : -Infinity;
        const hi = i + 1 < l.keys.length ? x.get(l.keys[i + 1]!)! - apart(k, l.keys[i + 1]!) : Infinity;
        let best = { at: now, score: straight(k, now) };
        for (const n of [...(up.get(k) ?? []), ...(down.get(k) ?? [])]) {
          const at = x.get(n)!;
          if (at < lo - 1e-9 || at > hi + 1e-9 || Math.abs(at - now) < 1e-6) continue;
          const score = straight(k, at);
          if (score > best.score) best = { at, score };
        }
        if (best.at !== now) {
          x.set(k, best.at);
          moved = true;
        }
      });
  }
  // Two lines that must cross, where one comes down right over the step (or lane) the other goes into: that one moved a
  // little aside (a long line's lanes all together), so the two cross once, plainly, and never run down one another.
  const laneChain = new Map<string, string[]>();
  for (const line of rows.lines) for (const v of line.via) laneChain.set(v, line.via);
  const rowOf = (k: string) => layers[layerOf.get(k)!]!.keys;
  const roomFor = (k: string, at: number) => {
    const r = rowOf(k);
    const i = r.indexOf(k);
    const lo = i > 0 ? x.get(r[i - 1]!)! + apart(r[i - 1]!, k) : -Infinity;
    const hi = i + 1 < r.length ? x.get(r[i + 1]!)! - apart(k, r[i + 1]!) : Infinity;
    return at >= lo - 1e-9 && at <= hi + 1e-9;
  };
  // A long line's lanes all at one place across, straight down, wherever every row it passes has room for it there:
  // the middle of where they are, else where one of them is, nearest that first.
  for (let pass = 0; pass < 2; pass++)
    for (const line of rows.lines) {
      const xs = line.via.map((v) => x.get(v)!);
      if (xs.length < 2 || Math.max(...xs) - Math.min(...xs) < 1e-6) continue;
      const mid = [...xs].sort((a, b) => a - b)[Math.floor(xs.length / 2)]!;
      const at = [mid, ...xs].sort((a, b) => Math.abs(a - mid) - Math.abs(b - mid)).find((c) => line.via.every((v) => roomFor(v, c)));
      if (at !== undefined) for (const v of line.via) x.set(v, at);
    }
  const aside = (k: string) => {
    if (middle.has(k)) return false;
    const group = laneChain.get(k) ?? [k];
    for (const d of [PART, -PART])
      if (group.every((g) => roomFor(g, x.get(g)! + d))) {
        for (const g of group) x.set(g, x.get(g)! + d);
        return true;
      }
    return false;
  };
  for (let pass = 0, moved = true; moved && pass < 3; pass++) {
    moved = false;
    for (const l of layers) {
      const links = l.keys.flatMap((a) => (down.get(a) ?? []).map((b) => [a, b] as const));
      for (const [a, b] of links)
        for (const [c, d] of links) {
          if (a === c || b === d) continue;
          if ((x.get(a)! - x.get(c)!) * (x.get(b)! - x.get(d)!) >= 0) continue;
          if (Math.abs(x.get(a)! - x.get(d)!) < 1e-6 && aside(d)) moved = true;
        }
    }
  }
  // The rows between two held in the middle (a phase between its gates) moved over together, when they reach a column or
  // more further out one side of the middle than the other, so the drawing is no wider than it need be; with nothing
  // held in the middle, the middle of what is drawn.
  const reachOf = (k: string, v: number) => (lane(k) ? [v + 0.25, v - 0.25] : [v - 0.5, v + 0.5]);
  for (let l = 0; l < layers.length; ) {
    let z = l;
    while (z < layers.length && !layers[z]!.keys.some((k) => middle.has(k))) z++;
    const free = layers.slice(l, z).flatMap((r) => r.keys);
    if (free.length) {
      const ends = free.flatMap((k) => reachOf(k, x.get(k)!));
      const shift = (Math.min(...ends) + Math.max(...ends)) / 2;
      if (Math.abs(shift) >= 0.5 || l === 0 && z === layers.length) for (const k of free) x.set(k, x.get(k)! - shift);
    }
    l = z + 1;
  }
  // A lane may lie a quarter column further out than the outermost step's middle, no further.
  const reach = Math.max(0, ...[...x].map(([k, v]) => (lane(k) ? Math.abs(v) - 0.25 : Math.abs(v))));
  const mid = Math.max((Math.max(1, Math.floor(cols)) - 1) / 2, reach);
  const col = (k: string) => Math.round((x.get(k)! + mid) * 1000) / 1000;
  const lanePlace = (k: string): GridLanePlace => {
    const where = layers[layerOf.get(k)!]!.at;
    return typeof where === 'string' ? { block: -1, row: 0, col: col(k) } : { block: where.group, row: where.row, col: col(k) };
  };
  return {
    cols: 2 * mid + 1,
    start: rows.start ? { key: rows.start, col: col(rows.start) } : null,
    end: rows.end ? { key: rows.end, col: col(rows.end) } : null,
    blocks: rows.groups.map((g) => ({ phase: g.phase, lines: g.rows.map((keys, row) => ({ keys, cols: keys.map(col), row })) })),
    lanes: rows.lines.filter((l) => l.via.length).map((l) => ({ from: l.from, to: l.to, via: l.via.map(lanePlace) })),
  };
}

/**
 * What each step waits on, without what a longer way already says (study 10, lines C): Scoping → Quote is left out
 * where Scoping → Rough sketches → Quote is there. Only among the steps given.
 */
export function directNeeds(steps: readonly Pick<GridStep, 'key' | 'needs'>[]): Map<string, string[]> {
  const all = new Set(steps.map((s) => s.key));
  const succ = new Map<string, Set<string>>();
  for (const s of steps) for (const d of s.needs) if (all.has(d) && d !== s.key) succ.set(d, new Set([...(succ.get(d) ?? []), s.key]));
  const longer = (a: string, c: string) => {
    const stack = [...(succ.get(a) ?? [])].filter((x) => x !== c);
    const seen = new Set<string>();
    while (stack.length) {
      const x = stack.pop()!;
      if (x === c) return true;
      if (seen.has(x)) continue;
      seen.add(x);
      stack.push(...(succ.get(x) ?? []));
    }
    return false;
  };
  return new Map(steps.map((s) => [s.key, [...new Set(s.needs)].filter((d) => all.has(d) && d !== s.key && !longer(d, s.key))]));
}

/**
 * A step opened, its chain alone (study 10, open; Factorio's tree): what it waits on above it, a row for each step
 * back, each step in the row furthest back it is waited on from; what waits on it below, the same way on. Rows run
 * outward from the step (`needs[0]` is right above it), each ordered across to sit near what it joins in the row
 * nearer the step. `limit` caps how many rows each way.
 */
export function chainRows(steps: readonly Pick<GridStep, 'key' | 'needs'>[], focus: string, limit = Infinity): { needs: string[][]; unlocks: string[][] } {
  const index = new Map(steps.map((s, i) => [s.key, i]));
  const needsOf = new Map(steps.map((s) => [s.key, s.needs.filter((d) => index.has(d))]));
  const dependents = new Map<string, string[]>();
  for (const s of steps) for (const d of needsOf.get(s.key)!) dependents.set(d, [...(dependents.get(d) ?? []), s.key]);
  const reach = (next: (k: string) => readonly string[]) => {
    const depth = new Map<string, number>();
    const visit = (k: string, d: number, path: Set<string>) => {
      if (d > limit) return;
      for (const m of next(k)) {
        if (m === focus || path.has(m)) continue;
        if ((depth.get(m) ?? 0) >= d) continue;
        depth.set(m, d);
        path.add(m);
        visit(m, d + 1, path);
        path.delete(m);
      }
    };
    visit(focus, 1, new Set([focus]));
    const rows: string[][] = [];
    for (const [k, d] of depth) (rows[d - 1] ??= []).push(k);
    return rows.filter(Boolean).map((r) => r.sort((a, b) => index.get(a)! - index.get(b)!));
  };
  const order = (rows: string[][], toward: (k: string) => readonly string[]) => {
    for (let r = 1; r < rows.length; r++) {
      const pos = new Map(rows[r - 1]!.map((k, i) => [k, i]));
      const bary = (k: string) => {
        const ds = toward(k).filter((d) => pos.has(d));
        return ds.length ? ds.reduce((s, d) => s + pos.get(d)!, 0) / ds.length : Number.MAX_SAFE_INTEGER;
      };
      rows[r] = [...rows[r]!].sort((a, b) => bary(a) - bary(b) || index.get(a)! - index.get(b)!);
    }
    return rows;
  };
  const needs = order(
    reach((k) => needsOf.get(k) ?? []),
    (k) => dependents.get(k) ?? [],
  );
  const unlocks = order(
    reach((k) => dependents.get(k) ?? []),
    (k) => needsOf.get(k) ?? [],
  );
  return { needs, unlocks };
}
